import { useContext, useMemo, useState } from 'react';
import { useStore } from 'store/store';
import moment from 'moment-timezone';
import { AxisItem } from '@/design-system/components/charts/axis/useAxis.hook';
import { useUtxEarnSubscriptionsGetAnalytics } from '@utx/utx-client';
import BigNumber from 'bignumber.js';
import { LayoutContext } from 'contexts/LayoutContext';
import { formatAmountByDecimals } from '@/utils/numberFormatting';

export type Analytics = {
  analytics: {
    $1d: number;
    $7d: number;
    $30d: number;
  };
  breakdown: Array<{
    date: string;
    amount: number;
  }>;
};

export enum AnalyticRange {
  $7Day = 7,
  $30Day = 30,
  $90Day = 90
}

interface UseMyEarningsParams {}

export const useMyEarnings = (params: UseMyEarningsParams) => {
  const store = useStore();
  const CURRENCY = store.baseCurrency;
  const layout = useContext(LayoutContext);

  const [analyticRange, setAnalyticRange] = useState<AnalyticRange>(AnalyticRange.$7Day);

  const query = useUtxEarnSubscriptionsGetAnalytics<{ data: Analytics }>(
    {
      currency: CURRENCY,
      from: moment()
        .subtract(analyticRange - 1, 'days')
        .format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD')
    },
    {
      axios: {
        headers: {
          Authorization: `Bearer ${store.authToken}`
        }
      }
    }
  );

  function changeDateRange(value: AnalyticRange) {
    setAnalyticRange(value);
  }

  const barChartData = useMemo(() => {
    const breakdown = query.data?.data?.breakdown;
    if (!breakdown) return [];

    const breakdownEveryNIndex = {
      [AnalyticRange.$7Day]: 1,
      [AnalyticRange.$30Day]: layout.isMobile ? 5 : 3,
      [AnalyticRange.$90Day]: layout.isMobile ? 15 : 9
    };

    // selec data points from the last index to the first index
    const selectedBreakdown = breakdown
      .slice()
      .reverse()
      .filter((_, index) => index % breakdownEveryNIndex[analyticRange] === 0)
      .reverse();

    return selectedBreakdown.map((item) => ({
      label: moment(item.date).format('MM-DD'),
      value: new BigNumber(item.amount).toNumber()
    }));
  }, [query.data?.data]);

  return {
    barChartData,
    query,
    analyticRange,
    overview: query.data?.data.analytics,
    currency: CURRENCY,
    showAmount: store.showAmount,
    loading: (query.isLoading || query.isFetching) && !query.data?.data,
    changeDateRange
  };
};
